import cs from "classnames";
import { remove } from "ramda";
import React from "react";
import { FaTimes } from "react-icons/fa";
import { defineMessages, FormattedMessage } from "react-intl";

import { Button } from "src/common/components/Button";
import { useOrgModParams } from "src/features/orgmod";

import "./FileInput.css";

const messages = defineMessages({
	selectFiles: {
		id: "file_input.select_files",
		defaultMessage: "Fájlok kiválasztása"
	}
});

const noop = (): void => {};

interface FileInputProps {
	className?: string;
	disabled?: boolean;
	savedFiles?: string[];
	onChangeSavedFiles?(files: string[]): unknown;
	unsavedFiles?: File[];
	onChangeUnsavedFiles?(files: File[]): unknown;
}

export const FileInput: React.FunctionComponent<FileInputProps> = ({
	className,
	disabled,
	savedFiles = [],
	onChangeSavedFiles = noop,
	unsavedFiles = [],
	onChangeUnsavedFiles: onChangeFiles = noop
}) => {
	const inputRef = React.useRef<HTMLInputElement>(null);

	// biome-ignore lint/correctness/useExhaustiveDependencies: reset native input when unsavedFiles changes so the same file can be re-selected
	React.useEffect(() => {
		if (inputRef.current !== null) {
			inputRef.current.value = "";
		}
	}, [unsavedFiles]);

	const onChange = React.useCallback(
		({ target: { files } }: React.ChangeEvent<HTMLInputElement>) => {
			onChangeFiles(files === null ? [] : Array.from(files));
		},
		[onChangeFiles]
	);

	const onClick = React.useCallback(() => {
		if (inputRef.current !== null) {
			inputRef.current.click();
		}
	}, []);

	return (
		<div
			className={cs("file-input", className, {
				"file-input--disabled": disabled
			})}
		>
			<input
				ref={inputRef}
				multiple={true}
				type="file"
				disabled={disabled}
				onChange={onChange}
			/>
			<Button
				type="button"
				variant="secondary"
				disabled={disabled}
				onClick={onClick}
			>
				<FormattedMessage {...messages.selectFiles} />
			</Button>
			{savedFiles.length !== 0 && (
				<div className="file-list file-list--saved">
					{savedFiles.map((name, index) => (
						<SavedFileListItem
							key={index}
							index={index}
							disabled={disabled}
							name={name}
							remove={index => {
								onChangeSavedFiles(
									remove(index, 1, savedFiles)
								);
							}}
						/>
					))}
				</div>
			)}
			{unsavedFiles.length !== 0 && (
				<div className="file-list file-list--unsaved">
					{unsavedFiles.map(({ name }, index) => (
						<UnsavedFileListItem
							key={index}
							index={index}
							disabled={disabled}
							name={name}
							remove={index => {
								onChangeFiles(remove(index, 1, unsavedFiles));
							}}
						/>
					))}
				</div>
			)}
		</div>
	);
};

interface FileListItemProps {
	index: number;
	disabled?: boolean;
	name: string;
	remove(index: number): unknown;
}

const SavedFileListItem: React.FunctionComponent<FileListItemProps> = ({
	index,
	disabled,
	name,
	remove
}) => {
	const { org, mod } = useOrgModParams();
	const onRemove = React.useCallback(() => {
		remove(index);
	}, [remove, index]);
	return (
		<div className="file-list__item">
			<div>
				<a
					href={`/.netlify/functions/files/${name}?organization=${org}&module=${mod}`}
					rel="noopener noreferrer"
					target="_blank"
				>
					{getOriginalFileName(name)}
				</a>
			</div>
			<Button
				type="button"
				disabled={disabled}
				variant="icon"
				onClick={onRemove}
			>
				<FaTimes />
			</Button>
		</div>
	);
};

const UnsavedFileListItem: React.FunctionComponent<FileListItemProps> = ({
	index,
	disabled,
	name,
	remove
}) => {
	const onRemove = React.useCallback(() => {
		remove(index);
	}, [remove, index]);
	return (
		<div className="file-list__item">
			<div>{name}</div>
			<Button
				type="button"
				disabled={disabled}
				variant="icon"
				onClick={onRemove}
			>
				<FaTimes />
			</Button>
		</div>
	);
};

function getOriginalFileName(name: string): string {
	const parts = name.split("/");
	return parts[parts.length - 1];
}
